'use client';

import { useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n/client';
import { sample, shuffle, WORD_PAIRS, type WordPair } from '@/content/games';
import { playCorrect, playLose, playStreak, playTick, playWin, playWrong } from '@/lib/sound';
import { Hearts } from './Hearts';
import type { GameFinish } from './WordMatch';

const DURATION = 60;
/** Wrong answers allowed before the round ends early (on top of the clock). */
const MAX_MISTAKES = 3;

function makeQuestion(pairs: WordPair[]): { pair: WordPair; options: string[] } {
  const picked = sample(pairs, Math.min(4, pairs.length));
  const [pair, ...distractors] = picked;
  return { pair: pair!, options: shuffle([pair!.rw, ...distractors.map((d) => d.rw)]) };
}

/** Timed drill: pick the correct Kinyarwanda meaning of each English word. */
export function WordRush({
  onFinish,
  pairs = WORD_PAIRS,
}: {
  onFinish: (result: GameFinish) => void;
  pairs?: WordPair[];
}) {
  const t = useT();
  const [stage, setStage] = useState<'intro' | 'play' | 'done'>('intro');
  const [question, setQuestion] = useState(() => makeQuestion(pairs));
  const [correct, setCorrect] = useState(0);
  const [answered, setAnswered] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [outOfHearts, setOutOfHearts] = useState(false);
  const [timeLeft, setTimeLeft] = useState(DURATION);
  const [picked, setPicked] = useState<string | null>(null);
  const finishRef = useRef(onFinish);

  useEffect(() => {
    finishRef.current = onFinish;
  }, [onFinish]);

  useEffect(() => {
    if (stage !== 'play') return;
    let remaining = DURATION;
    setTimeLeft(remaining);
    const id = setInterval(() => {
      remaining -= 1;
      setTimeLeft(remaining);
      if (remaining > 0 && remaining <= 5) playTick();
      if (remaining <= 0) {
        clearInterval(id);
        setStage('done');
      }
    }, 1000);
    return () => clearInterval(id);
  }, [stage]);

  useEffect(() => {
    if (stage === 'done') {
      playWin();
      finishRef.current({ correct, total: answered, score: correct * 10 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  function answer(option: string) {
    if (picked) return;
    setPicked(option);
    setAnswered((n) => n + 1);
    if (option === question.pair.rw) {
      setCorrect((n) => n + 1);
      const nextStreak = streak + 1;
      setStreak(nextStreak);
      setBestStreak((b) => Math.max(b, nextStreak));
      // Celebrate every 5-in-a-row with a brighter cue.
      if (nextStreak % 5 === 0) playStreak();
      else playCorrect();
    } else {
      setStreak(0);
      const nextMistakes = mistakes + 1;
      setMistakes(nextMistakes);
      if (nextMistakes >= MAX_MISTAKES) {
        playLose();
        setOutOfHearts(true);
        window.setTimeout(() => setStage('done'), 500);
        return;
      }
      playWrong();
    }
    window.setTimeout(() => {
      setPicked(null);
      setQuestion(makeQuestion(pairs));
    }, 320);
  }

  function start() {
    setCorrect(0);
    setAnswered(0);
    setStreak(0);
    setBestStreak(0);
    setMistakes(0);
    setOutOfHearts(false);
    setQuestion(makeQuestion(pairs));
    setStage('play');
  }

  if (stage === 'intro') {
    return (
      <div className="vn-game2 vn-game2--center">
        <p className="vn-game2__intro">{t('games.rushIntro', { seconds: DURATION })}</p>
        <button type="button" className="vn-btn vn-btn--primary" onClick={start}>
          {t('games.start')}
        </button>
      </div>
    );
  }

  if (stage === 'done') {
    return (
      <div className="vn-game2 vn-game2--center">
        <p className="vn-game2__result-title">
          {outOfHearts ? t('games.outOfHearts') : t('games.rushDone', { correct })}
        </p>
        <p className="vn-game2__status">{t('games.score', { score: correct * 10 })}</p>
        {bestStreak >= 3 ? (
          <p className="vn-game2__status">🔥 {t('games.bestStreak', { count: bestStreak })}</p>
        ) : null}
        <button type="button" className="vn-btn vn-btn--primary" onClick={start}>
          {t('games.playAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="vn-game2">
      <div className="vn-rush__bar">
        <span className={`vn-rush__time${timeLeft <= 10 ? ' is-low' : ''}`}>{t('games.timeLeft', { time: timeLeft })}</span>
        {streak >= 2 ? (
          <span className="vn-rush__streak" aria-live="polite">
            🔥 {t('games.streak', { count: streak })}
          </span>
        ) : null}
        <Hearts mistakes={mistakes} max={MAX_MISTAKES} />
        <span className="vn-rush__score">{t('games.score', { score: correct * 10 })}</span>
      </div>
      <p className="vn-rush__prompt">{question.pair.en}</p>
      <div className="vn-rush__options">
        {question.options.map((option) => {
          // Only colour the option the learner picked — never reveal the right
          // answer when they get it wrong.
          const tone =
            picked && option === picked
              ? option === question.pair.rw
                ? ' is-correct'
                : ' is-wrong'
              : '';
          return (
            <button
              key={option}
              type="button"
              className={`vn-rush__option${tone}`}
              disabled={Boolean(picked)}
              onClick={() => answer(option)}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}
